import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  Form,
  Button,
  SelectPicker,
  Stack,
  Message,
  useToaster,
  CheckPicker
} from 'rsuite';
import { useDispatch, useSelector } from 'react-redux';
import { getAdminUsers } from '@/services/user.service';

interface User {
  _id: string;
  firstname: string;
  lastname: string;
  registrationNumber: string;
  forgotten?: boolean;
}

interface DrawerViewProps {
  open: boolean;
  onClose: () => void;
  onFiltersChange: (filters: any) => void;
  onResetFilters: () => void;
  currentFilters: {
    userId: string;
    operationOwnerId: string;
    authorizedDays: string[];
    transactionType: string;
  };
  users: User[];
  usersLoading: boolean;
}

const DrawerView: React.FC<DrawerViewProps> = ({
  open,
  onClose,
  onFiltersChange,
  onResetFilters,
  currentFilters,
  users,
  usersLoading
}) => {
  const dispatch = useDispatch();
  const toaster = useToaster();
  const [formValue, setFormValue] = React.useState(currentFilters);
  const { t } = useTranslation();

  const userOptions = React.useMemo(() => {
    return (
      users
        .filter(user => user.forgotten !== true)
        .map(user => ({
          label: `${user.firstname} ${user.lastname} (${user.registrationNumber})`,
          value: user._id
        })) || []
    );
  }, [users]);

  const transactionTypeOptions = [
    { label: t('ALL'), value: '' },
    { label: t('Submission'), value: 'submission' },
    { label: t('Reject'), value: 'reject' },
    { label: t('Cancellation'), value: 'cancellation' }
  ];

  const daysOptions = [
    { label: t('Monday'), value: 'Monday' },
    { label: t('Tuesday'), value: 'Tuesday' },
    { label: t('Wednesday'), value: 'Wednesday' },
    { label: t('Thursday'), value: 'Thursday' },
    { label: t('Friday'), value: 'Friday' },
    { label: t('Saturday'), value: 'Saturday' },
    { label: t('Sunday'), value: 'Sunday' }
  ];

  const handleSubmit = () => {
    const hasFilters = Object.values(formValue).some(value => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== '' && value !== null;
    });

    if (hasFilters) {
      onFiltersChange(formValue);
      toaster.push(
        <Message type="success" duration={3000}>
          {t('Filters applied successfully!')}
        </Message>,
        { placement: 'topCenter' }
      );
    } else {
      onResetFilters();
    }

    onClose();
  };

  const handleReset = () => {
    setFormValue({
      userId: '',
      operationOwnerId: '',
      authorizedDays: [],
      transactionType: ''
    });
    onResetFilters();
    toaster.push(
      <Message type="info" duration={3000}>
        {t('Filters have been reset!')}
      </Message>,
      { placement: 'topCenter' }
    );
    onClose();
  };

  useEffect(() => {
    if (open) {
      setFormValue(currentFilters);
    }
  }, [open, currentFilters]);

  return (
    <Drawer backdrop="static" size="sm" placement="right" onClose={onClose} open={open}>
      <Drawer.Header>
        <Drawer.Title>{t('Filter Options')}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleReset} appearance="subtle">
            {t('Reset')}
          </Button>
          <Button onClick={handleSubmit} appearance="primary">
            {t('Apply')}
          </Button>
          <Button onClick={onClose} appearance="subtle">
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form fluid formValue={formValue} onChange={setFormValue}>
          <Form.Group>
            <Form.ControlLabel>{t('User')}</Form.ControlLabel>
            <Form.Control
              name="userId"
              accepter={SelectPicker}
              data={userOptions}
              loading={usersLoading}
              searchable
              placeholder={t('Select user')}
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Operation Owner')}</Form.ControlLabel>
            <Form.Control
              name="operationOwnerId"
              accepter={SelectPicker}
              data={userOptions}
              loading={usersLoading}
              searchable
              placeholder={t('Select operation owner')}
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Authorized Days')}</Form.ControlLabel>
            <Form.Control
              name="authorizedDays"
              accepter={CheckPicker}
              data={daysOptions}
              searchable={false}
              placeholder={t('Select authorized days')}
              block
              placement="auto"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Transaction Type')}</Form.ControlLabel>
            <Form.Control
              name="transactionType"
              accepter={SelectPicker}
              data={transactionTypeOptions}
              searchable={false}
              placeholder={t('Select transaction type')}
              block
            />
          </Form.Group>
        </Form>
      </Drawer.Body>
    </Drawer>
  );
};

export default DrawerView;
